import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import * as moment from 'moment';
import { AppService } from '@Core/services/proprietary/app.service';
import { StateHelperService } from '@Core/services/proprietary/state-helper.service';
import { cloneDeep } from 'src/ep-utils';
import { SelfAuditCalculateReporting } from './contract-object.actions';
import { TransactionNodeType } from './contract-object.types';
import { UpdateContractProcedure } from './contract-procedure.actions';
import { SyncContractNumber } from './contract.actions';
import { ContractState } from './contract.state';

export interface ContractProcedureStateModel {
    procedure: any;
    procedureType: string;
    currentID: string;
}

@State<ContractProcedureStateModel>({
    name: 'contractProcedureState',
    defaults: {
        procedure: {},
        procedureType: null,
        currentID: null
    }
})
@Injectable()
export class ContractProcedureState {
    constructor(private helper: StateHelperService, private store: Store, private app: AppService) { }

    @Selector()
    static procedure(state: ContractProcedureStateModel) {
        return state.procedure[state.currentID] || {};
    }

    @Selector()
    static procedureType(state: ContractProcedureStateModel) {
        return state.procedureType || '';
    }

    set1201Time(data: string): string {
        if (data) {
            if (this.app.getBookOptionValue('AutoSet_1201AM_On_ContProcs_Ind') == 'Y') {
                data = data.replace('T00:00', 'T00:01');
            }
        }
        return data;
    }

    @Action(SyncContractNumber)
    syncProcedureContractNumber(sc: StateContext<ContractProcedureStateModel>, action: SyncContractNumber) {
        const state = sc.getState();
        if (!state.currentID) {
            return;
        }
        const procID = state.currentID;
        const procedureType = state.procedureType;
        const currentProc = state.procedure[procID];
        let updateProc = cloneDeep(currentProc);

        switch (procedureType) {
            case TransactionNodeType.Renewal:
                if (currentProc.Renew_Contract_Num == action.contractNumber) {
                    return;
                }
                updateProc.Renew_Contract_Num = action.contractNumber;
                break;

            case TransactionNodeType.Reissue:
            case TransactionNodeType.Rewrite:
                if (currentProc.New_Contract_Num == action.contractNumber) {
                    return;
                }
                updateProc.New_Contract_Num = action.contractNumber;
                break;

            default:
                return;
        }

        sc.patchState({
            procedure: {
                ...state.procedure,
                [procID]: { ...currentProc, ...updateProc }
            }
        });
    }

    @Action(UpdateContractProcedure)
    updateContractProcedure(sc: StateContext<ContractProcedureStateModel>, action: UpdateContractProcedure) {
        const state = sc.getState();
        const procID = state.currentID;
        const procedureType = state.procedureType;
        const currentProc = state.procedure[procID];
        let updateProc: any = { ...action.procedure }; //need DEEP copy?
        this.helper.setItemChanged(updateProc, currentProc.Edit_Status);

        switch (procedureType) {
            case TransactionNodeType.ScheduleCancel:
                // Checking for Cancel_Date change must occur before Cancel_Method. 
                // Changing Method to Flat changes Cancel_Date and we don't want to change 12:00 to 12:01 when using the Off_Eff_Date
                if (this.helper.stateValueChanged(updateProc.Cancel_Date, currentProc.Cancel_Date)) {
                    updateProc.Cancel_Date = this.set1201Time(updateProc.Cancel_Date);
                }

                if (
                    this.helper.stateValueChanged(updateProc.Cancel_Method, currentProc.Cancel_Method) &&
                    updateProc.Cancel_Method == 'F'
                ) {
                    if (updateProc.Org_Eff_Date) {
                        updateProc.Cancel_Date = updateProc.Org_Eff_Date;
                    }
                }

                break;

            case TransactionNodeType.Audit:
                if (
                    this.helper.stateValueChanged(updateProc.Audit_Type, currentProc.Audit_Type) &&
                    (currentProc.Audit_Type == 'O' || currentProc.Audit_Type == 'F')
                ) {
                    updateProc.Audit_Provider = { Entity_ID: '', Entity_Name: '' };
                }

                if (this.helper.stateValueChanged(updateProc.Audit_Received_Date, currentProc.Audit_Received_Date)) {
                    updateProc.Audit_Received_Date = this.set1201Time(updateProc.Audit_Received_Date);
                }

                if (this.helper.stateValueChanged(updateProc.Audit_Completion_Date, currentProc.Audit_Completion_Date)) {
                    updateProc.Audit_Completion_Date = this.set1201Time(updateProc.Audit_Completion_Date);
                }

                if (this.helper.stateValueChanged(updateProc.Audit_Statement_Date, currentProc.Audit_Statement_Date)) {
                    updateProc.Audit_Statement_Date = this.set1201Time(updateProc.Audit_Statement_Date);
                }

                break;

            case TransactionNodeType.SelfAudit:
                //Check for change to AutoCalc_Type
                if (this.helper.stateValueChanged(updateProc.AutoCalc_Type, currentProc.AutoCalc_Type)) {
                    if (updateProc.AutoCalc_Type == 'D' || updateProc.AutoCalc_Type == 'S') {
                        const contract = this.store.selectSnapshot(ContractState.contract);

                        updateProc.Round_AutoCalc_Amt_Ind = 'Y';

                        let reportingFreq = contract.Reporting_Freq;
                        switch (reportingFreq) {
                            case 'A':
                                updateProc.AutoCalc_Pct = '100';
                                break;
                            case 'M':
                                let effDate = moment(contract.Eff_Date, 'YYYY-MM-DD');
                                let expDate = moment(contract.Exp_Date, 'YYYY-MM-DD');
                                let monthDiff = expDate.diff(effDate, 'M');
                                if (!monthDiff || monthDiff < 1) {
                                    monthDiff = 1;
                                }
                                updateProc.AutoCalc_Pct = (100 / monthDiff).toString();
                                break;
                            case 'O':
                                updateProc.AutoCalc_Pct = '0';
                                break;
                            case 'S':
                                updateProc.AutoCalc_Pct = '50';
                                break;
                            case 'Q':
                                updateProc.AutoCalc_Pct = '25';
                                break;
                            default:
                                updateProc.AutoCalc_Pct = '0';
                                break;
                        }

                        this.store.dispatch(
                            new SelfAuditCalculateReporting(
                                updateProc.AutoCalc_Type,
                                updateProc.AutoCalc_Pct,
                                updateProc.Round_AutoCalc_Amt_Ind == 'Y'
                            )
                        );
                    } else {
                        updateProc.AutoCalc_Pct = '0';
                        updateProc.Round_AutoCalc_Amt_Ind = 'N';
                    }
                } else {
                    //Placed in else so to Pct and Round_Ind from change Type doesn't trigger calc twice.
                    if (this.helper.stateValueChanged(updateProc.AutoCalc_Pct, currentProc.AutoCalc_Pct)) {
                        this.store.dispatch(
                            new SelfAuditCalculateReporting(
                                currentProc.AutoCalc_Type,
                                updateProc.AutoCalc_Pct,
                                currentProc.Round_AutoCalc_Amt_Ind == 'Y'
                            )
                        );
                    }

                    if (this.helper.stateValueChanged(updateProc.Round_AutoCalc_Amt_Ind, currentProc.Round_AutoCalc_Amt_Ind)) {
                        this.store.dispatch(
                            new SelfAuditCalculateReporting(
                                currentProc.AutoCalc_Type,
                                currentProc.AutoCalc_Pct,
                                updateProc.Round_AutoCalc_Amt_Ind == 'Y'
                            )
                        );
                    }
                }

                if (this.helper.stateValueChanged(updateProc.Reporting_Date, currentProc.Reporting_Date)) {
                    //Not Currently possible. Can't be changed once picked from template edit-panel. Adding here as a back up.
                    updateProc.Reporting_Date = this.set1201Time(updateProc.Reporting_Date);
                }

                break;

            case TransactionNodeType.ExtendTerm:
                if (this.helper.stateValueChanged(updateProc.New_Exp_Date, currentProc.New_Exp_Date)) {
                    //Not Currently possible. Can't be changed once picked from template edit-panel. Adding here as a back up.
                    updateProc.Total_Endorsement_Prem = '';
                    updateProc.New_Exp_Date = this.set1201Time(updateProc.New_Exp_Date);
                }
                break;

            case TransactionNodeType.Cancel:
                if (this.helper.stateValueChanged(updateProc.Cancel_Date, currentProc.Cancel_Date)) {
                    //Not Currently possible. Can't be changed once picked from template edit-panel. Adding here as a back up.
                    updateProc.Cancel_Date = this.set1201Time(updateProc.Cancel_Date);
                }
                break;

            case TransactionNodeType.ChangeConsumer:
                if (this.helper.stateValueChanged(updateProc.Endorse_Eff_Date, currentProc.Endorse_Eff_Date)) {
                    updateProc.Endorse_Eff_Date = this.set1201Time(updateProc.Endorse_Eff_Date);
                }
                break;

            case TransactionNodeType.Endorsement:
                if (this.helper.stateValueChanged(updateProc.Endorse_Eff_Date, currentProc.Endorse_Eff_Date)) {
                    //Not Currently possible. Can't be changed once picked from template edit-panel. Adding here as a back up.
                    updateProc.Endorse_Eff_Date = this.set1201Time(updateProc.Endorse_Eff_Date);
                }
                break;

            case TransactionNodeType.Rewrite:
                if (this.helper.stateValueChanged(updateProc.Last_Cancel_Date, currentProc.Last_Cancel_Date)) {
                    //Not Currently possible. Last_Cancel_Date not supported in SDK or SureMGA yet. Adding here as a back up.
                    updateProc.Last_Cancel_Date = this.set1201Time(updateProc.Last_Cancel_Date);
                }
                if (this.helper.stateValueChanged(updateProc.New_Contract_Num, currentProc.New_Contract_Num)) {
                    sc.dispatch(new SyncContractNumber(updateProc.New_Contract_Num));
                }
                break;
            case TransactionNodeType.Renewal:
                if (this.helper.stateValueChanged(updateProc.Renew_Contract_Num, currentProc.Renew_Contract_Num)) {
                    sc.dispatch(new SyncContractNumber(updateProc.Renew_Contract_Num));
                }
                break;
            case TransactionNodeType.Reissue:
                if (this.helper.stateValueChanged(updateProc.New_Contract_Num, currentProc.New_Contract_Num)) {
                    sc.dispatch(new SyncContractNumber(updateProc.New_Contract_Num));
                }
                break;
        }

        sc.patchState({
            procedure: {
                ...state.procedure,
                [procID]: { ...currentProc, ...updateProc }
            }
        });
    }
}
